import type { ApiError } from '@/types';
import { refreshAuthToken } from './tokenRefresher';

/**
 * Resolved API base URL — the value of `VITE_API_URL` if set, otherwise the
 * relative `/api` so the dev proxy / nginx in prod can route. Exported so
 * services that bypass `request()` (e.g. raw `fetch` for SSE streams) can
 * build their own URLs against the same root.
 */
export const BASE_URL: string = (() => {
  const env =
    typeof import.meta !== 'undefined' && import.meta.env
      ? (import.meta.env as Record<string, string>)
      : {};
  const url = env['VITE_API_URL'];
  return url ? url : '/api';
})();

/** API host with the trailing `/api` stripped — useful when building media URLs that live at the host root. */
export const serverRootUrl = BASE_URL.startsWith('http') ? BASE_URL.replace(/\/api$/, '') : '';

/** Reads the current auth token from localStorage. SSR-safe (returns null when window is undefined). */
export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
}

let globalLogoutHandler: (() => void) | null = null;

/**
 * Registers the function `request()` should call when an authenticated request
 * gets a 401 it cannot recover from (i.e. token refresh also failed).
 * `AuthProvider` calls this on mount so module-level `request()` can trigger logout.
 */
export function setGlobalLogoutHandler(handler: () => void) {
  globalLogoutHandler = handler;
}

type RequestOptions = globalThis.RequestInit & { auth?: boolean };

function buildHeaders(headers: HeadersInit | undefined, _auth: boolean): Headers {
  const finalHeaders = new Headers(headers || {});
  if (!finalHeaders.has('Accept')) finalHeaders.set('Accept', 'application/json');
  return finalHeaders;
}

function setContentTypeIfNeeded(headers: Headers, body: BodyInit | null | undefined): void {
  const hasBody = body !== undefined && body !== null;
  if (hasBody && !(body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
}

function attachAuthToken(headers: Headers): void {
  const token = getToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), {
      status: 401,
      isNetworkError: false,
    });
  }
  headers.set('Authorization', `Bearer ${token}`);
}

function buildUrl(path: string): string {
  return path.startsWith('http') ? path : `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

function handleNetworkError(networkError: unknown): never {
  if (networkError instanceof Error && networkError.name === 'AbortError') {
    throw networkError;
  }
  throw Object.assign(new Error('Cannot connect to server. Please check your connection.'), {
    status: 0,
    isNetworkError: true,
    originalError: networkError,
  });
}

async function attemptTokenRefresh<T>(
  url: string,
  finalHeaders: Headers,
  rest: Omit<RequestOptions, 'auth' | 'headers'>
): Promise<T | null> {
  const newToken = await refreshAuthToken();
  if (!newToken) return null;

  const retryHeaders = new Headers(finalHeaders);
  retryHeaders.set('Authorization', `Bearer ${newToken}`);
  const retryResp = await fetch(url, { ...rest, headers: retryHeaders });

  if (!retryResp.ok) return null;
  if (retryResp.status === 204) return undefined as unknown as T;
  return (await retryResp.json()) as T;
}

function buildErrorFromResponse(errBody: unknown, resp: Response): never {
  const error = (errBody as Record<string, unknown>) ?? {
    message: resp.statusText,
    status: resp.status,
  };
  throw Object.assign(
    new Error(String((error as Record<string, unknown>)['message'] ?? resp.statusText)),
    { ...error, status: resp.status, isNetworkError: false }
  );
}

/**
 * Core HTTP wrapper used by every service. JSON-only body+response.
 * - `auth: true` attaches the bearer token; on 401 it tries one token refresh + retry,
 *   and falls back to the global logout handler.
 * - 204 responses resolve to `undefined` cast as `T`.
 * - Network failures and non-2xx responses throw an Error augmented with `status`,
 *   `isNetworkError`, and any `detail` from the server body — `getErrorMessage()`
 *   knows how to read them.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { auth, headers, ...rest } = options;

  const finalHeaders = buildHeaders(headers, !!auth);
  setContentTypeIfNeeded(finalHeaders, rest.body);
  if (auth) attachAuthToken(finalHeaders);

  const url = buildUrl(path);

  let resp: Response;
  try {
    resp = await fetch(url, { ...rest, headers: finalHeaders });
  } catch (networkError) {
    handleNetworkError(networkError);
  }

  if (!resp.ok) {
    if (resp.status === 401 && auth) {
      const retryResult = await attemptTokenRefresh<T>(url, finalHeaders, rest);
      if (retryResult !== null) return retryResult;
      if (globalLogoutHandler) setTimeout(() => globalLogoutHandler?.(), 100);
    }

    let errBody: unknown;
    try {
      errBody = await resp.json();
    } catch {
      /* ignore */
    }
    buildErrorFromResponse(errBody, resp);
  }

  if (resp.status === 204) return undefined as unknown as T;
  return (await resp.json()) as T;
}

/**
 * Authenticated fetch for streaming or binary responses that cannot use the
 * JSON-only `request()` helper. It mirrors `request()`'s one-time token refresh
 * so long-lived pages do not fail their next SSE request with an expired JWT.
 */
export async function fetchWithAuthRetry(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const fetchWithToken = async (token: string): Promise<Response> => {
    const headers = new Headers(init.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  };

  const token = getToken();
  if (!token) {
    throw Object.assign(new Error('Not authenticated'), {
      status: 401,
      isNetworkError: false,
    });
  }

  let response: Response;
  try {
    response = await fetchWithToken(token);
  } catch (networkError) {
    handleNetworkError(networkError);
  }

  if (response.status !== 401) return response;

  const newToken = await refreshAuthToken();
  if (!newToken) {
    if (globalLogoutHandler) setTimeout(() => globalLogoutHandler?.(), 100);
    return response;
  }

  try {
    return await fetchWithToken(newToken);
  } catch (networkError) {
    handleNetworkError(networkError);
  }
}

/**
 * Upload helper using XMLHttpRequest to support progress events.
 * Kept in client.ts as it's a low-level utility.
 */
export function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = path.startsWith('http')
      ? path
      : `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;

    xhr.open('POST', url);

    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve(response as T);
        } catch {
          reject({ message: 'Invalid JSON response', status: xhr.status });
        }
      } else {
        try {
          const errBody = JSON.parse(xhr.responseText);
          reject({ ...errBody, status: xhr.status });
        } catch {
          reject({ message: xhr.statusText, status: xhr.status });
        }
      }
    };

    xhr.onerror = () => reject({ message: 'Network error', isNetworkError: true, status: 0 });
    xhr.send(formData);
  });
}

function extractDetailMessage(detail: unknown): string | null {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] as { msg?: unknown };
    if (first?.msg) return String(first.msg);
  }
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    const obj = detail as Record<string, unknown>;
    if (typeof obj['message'] === 'string') return obj['message'];
  }
  return null;
}

function extractObjectErrorMessage(error: Record<string, unknown>): string {
  const e = error as Partial<ApiError & { status?: number; isNetworkError?: boolean }> &
    Record<string, unknown>;

  if (e.isNetworkError === true) {
    return 'Cannot connect to server. Please check your connection and try again.';
  }

  const detailMsg = e.detail ? extractDetailMessage(e.detail) : null;
  if (detailMsg) return detailMsg;

  if (e.message) return String(e.message);
  if (typeof e.status === 'number') {
    return e.status === 0 ? 'Cannot connect to server' : `Request failed with status ${e.status}`;
  }

  return 'An unexpected error occurred';
}

/**
 * Normalizes anything thrown by `request()` (or any of the services) into a
 * user-presentable string. Prefers backend `detail.message` over generic
 * `error.message` when present.
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) {
    // API errors have detail spread onto the Error object — prefer it over statusText message
    const detail = (error as unknown as Record<string, unknown>)['detail'];
    const detailMsg = detail ? extractDetailMessage(detail) : null;
    if (detailMsg) return detailMsg;
    return error.message;
  }
  if (error && typeof error === 'object') {
    return extractObjectErrorMessage(error as Record<string, unknown>);
  }
  return 'An unexpected error occurred';
}
